import { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Id,
  Identifiable,
  Resource,
  RetrieveWorkLoadsFilter,
  WorkLoad,
  WorkLoadsCalendar,
} from "@planzone/dto";
import { CoreContext } from "../context";

type View = RetrieveWorkLoadsFilter["view"];

type Chunk = {
  key: string;
  startDate: string;
  endDate: string;
};

interface ViewModel {
  workLoads: WorkLoadsCalendar;
  isAnyChunkPending: boolean;
  isChunkLoadedForResource(resourceUuid: Id, chunkKey: string): boolean;
  isChunkPendingForResource(resourceUuid: Id, chunkKey: string): boolean;
}

interface Params {
  view: View;
  resources: Identifiable<Resource>[];
  chunks: Chunk[];
}

function useChunkedWorkLoads({ view, resources, chunks }: Params): ViewModel {
  const { selector, core } = useContext(CoreContext);

  const resourceUuids = useMemo(
    () => resources.map((r) => r.uuid),
    [resources],
  );
  const chunkKeys = useMemo(() => chunks.map((c) => c.key), [chunks]);

  const workLoads = useSelector((): WorkLoadsCalendar => {
    const loaded = selector.getMergedWorkLoads(view, resourceUuids, chunkKeys);
    const loadedByUuid = new Map(loaded.map((wl) => [wl.uuid, wl]));
    return resources.map(
      (r): WorkLoad =>
        loadedByUuid.get(r.uuid) ?? {
          uuid: r.uuid,
          resourceName: r.name,
          resourceRoleName: r.role?.name ?? "",
          resourceTitle: r.profession || null,
          resourceType: r.type,
          resourceTags: r.tags.map(({ name }) => name),
          totalOfEstimatedHours: 0,
          totalOfAllocatedHours: 0,
          totalOfSpentTime: 0,
          totalOfRemainingTime: 0,
          days: [],
          weeks: [],
          months: [],
        },
    );
  });
  const isAnyChunkPending = useSelector(() =>
    selector.isAnyWorkLoadsChunkPending(view, resourceUuids, chunkKeys),
  );

  const fetchSignature = useSelector(() => {
    if (resources.length === 0 || chunks.length === 0) return "";
    let sig = "";
    for (const c of chunks) {
      for (const r of resources) {
        const loaded = selector.isWorkLoadsChunkLoaded(view, r.uuid, c.key);
        const pending = selector.isWorkLoadsChunkPending(view, r.uuid, c.key);
        const dirty = selector.isWorkLoadsChunkDirty(view, r.uuid, c.key);
        const eligible = (!loaded || dirty) && !pending;
        sig += eligible ? "1" : "0";
      }
    }
    return sig;
  });

  useEffect(() => {
    if (resources.length === 0) return;
    for (const chunk of chunks) {
      const missing = resources.filter((r) => {
        const loaded = selector.isWorkLoadsChunkLoaded(view, r.uuid, chunk.key);
        const pending = selector.isWorkLoadsChunkPending(
          view,
          r.uuid,
          chunk.key,
        );
        const dirty = selector.isWorkLoadsChunkDirty(view, r.uuid, chunk.key);
        return (!loaded || dirty) && !pending;
      });
      if (missing.length > 0) {
        core.retrieveWorkLoads({
          view,
          resources: missing,
          startDate: chunk.startDate,
          endDate: chunk.endDate,
          chunkKey: chunk.key,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSignature]);

  return {
    workLoads,
    isAnyChunkPending,
    isChunkLoadedForResource: (uuid, chunkKey) =>
      selector.isWorkLoadsChunkLoaded(view, uuid, chunkKey),
    isChunkPendingForResource: (uuid, chunkKey) =>
      selector.isWorkLoadsChunkPending(view, uuid, chunkKey),
  };
}

export { useChunkedWorkLoads };
export type { Chunk as ChunkedWorkLoadsChunk };
